import { useMemo, useState } from 'react';

import { FlexBox, IconButton, Image, Text } from '@blockworks/ui/components';
import { ChevronLeftIcon, ChevronRightIcon } from '@blockworks/ui/icon';

import assetsScreenshot from './assets/assets-screenshot.png';
import govhubScreenshot from './assets/govhub-screenshot.png';
import metricsScreenshot from './assets/metrics-screenshot.png';
import researchScreenshot from './assets/research-screenshot.png';

const screenshots = [
    { caption: 'Protocol user metrics and financials', img: metricsScreenshot },
    { caption: 'Custom dashboards curated by our team', img: assetsScreenshot },
    { caption: 'Crypto’s most powerful governance platform', img: govhubScreenshot },
    { caption: 'Deep protocol research and insights', img: researchScreenshot },
] as const;

const slides = screenshots.map(({ img, caption }) => (
    <FlexBox col alignItems="center" justifyContent="start" key={caption} minW="max">
        <Image src={img.src} alt={caption} width={500} height={435} />
        <div className="mt-[-18px]">
            <Text>{caption}</Text>
        </div>
    </FlexBox>
));

const Dots = ({
    currentSlide,
    onDotClick,
    onDotKeyDown,
}: {
    currentSlide: number;
    onDotClick: (slideIdx: number) => () => void;
    onDotKeyDown: (slideIdx: number) => React.KeyboardEventHandler;
}) => {
    return screenshots.map(({ caption }, idx) => (
        <div
            onClick={onDotClick(idx)}
            onKeyDown={onDotKeyDown(idx)}
            role="button"
            tabIndex={0}
            key={caption}
            className={`transition-all w-2 h-2 rounded-full ${currentSlide === idx ? 'bg-white' : 'bg-gray-600'}`}
        />
    ));
};

const SignupImageSlider = () => {
    const [currentSlide, setCurrentSlide] = useState(0);

    const handlers = useMemo(() => {
        return {
            prev: () => setCurrentSlide(curr => (curr === 0 ? screenshots.length - 1 : curr - 1)),
            next: () => setCurrentSlide(curr => (curr === screenshots.length - 1 ? 0 : curr + 1)),
            onDotClick: (curr: number) => () => setCurrentSlide(curr),
            onDotKeyDown: (curr: number) => (event: React.KeyboardEvent) => {
                if (event.key === 'Enter') setCurrentSlide(curr);
            },
        };
    }, []);

    return (
        <div className="flex flex-col max-w-[500px] mx-auto mr-[4.6rem] gap-0.5">
            <div className="overflow-hidden relative">
                <div
                    className="flex transition-transform ease-out duration-500"
                    style={{ transform: `translateX(-${currentSlide * 500}px)` }}
                >
                    {slides}
                </div>
            </div>
            <FlexBox justifyContent="between" alignItems="center" w={400} mx="auto">
                <IconButton variant="ghost" icon={ChevronLeftIcon} aria-label="previous" onClick={handlers.prev} />
                <FlexBox alignItems="center" justifyContent="center" gap="md">
                    <Dots
                        currentSlide={currentSlide}
                        onDotClick={handlers.onDotClick}
                        onDotKeyDown={handlers.onDotKeyDown}
                    />
                </FlexBox>
                <IconButton variant="ghost" icon={ChevronRightIcon} aria-label="next" onClick={handlers.next} />
            </FlexBox>
        </div>
    );
};

export { SignupImageSlider };
